import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
    deleteDigitalLaborLawPoster,
    updateDigitalLaborLawPoster,
} from '../../../utils/apiServices/digitalLaborLawPosterAPI';
import { getContentTypeByExtension, getFileExtension, popup } from '../../../utils';
import { DIGITAL_LABOR_LAW_POSTER, QUERY_KEYS, uptoContentManagerRole } from '../../../constants';
import { Delete, DownloadIcon, Edit, ViewIcon } from '../../../assets/svg';
import { DeleteConfirmationModal } from '../../shared';
import { EditFileNameModal } from '../../shared/Modal/EditFileNameModal';
import useFileHandler from '../../../hooks/useFileHandler';

const DigitalLaborLawPosterActions = ({ data }) => {
    const queryClient = useQueryClient();
    const { id, file_name, s3_key } = data;
    const extension = getFileExtension(s3_key);
    const fileType = getContentTypeByExtension(extension);
    const role = useSelector((s) => s.user.role);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const hasPrivileges = uptoContentManagerRole(role);
    const { viewHandler, downloadHandler } = useFileHandler({
        fileType,
        file_name,
        s3_key,
        extension,
    });

    const editHandler = () => {
        setShowEditModal(true);
    };

    const deleteDLPMutation = useMutation({
        mutationFn: async (selectedItemId) =>
            await deleteDigitalLaborLawPoster({ data: [{ id: selectedItemId || id, s3_key }] }),
        onSuccess: ({ message }) => {
            popup('success', message);
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.DIGITAL_LABOR_POSTERS],
                exact: false,
            });
            setIsDeleteModalOpen(false);
        },
        onError: (error) => popup('error', error.message),
    });

    const deleteHandler = async ({ selectedItemId }) => {
        deleteDLPMutation.mutate(selectedItemId);
    };

    const editDLPMutation = useMutation({
        mutationFn: async (data) => await updateDigitalLaborLawPoster({ data, id }),
        onSuccess: ({ message }) => {
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.DIGITAL_LABOR_POSTERS],
                exact: false,
            });
            popup('success', message);
            setShowEditModal(false);
        },
        onError: (err) => popup('error', err.message),
    });

    const editModalOnSubmit = async (values) => {
        editDLPMutation.mutate({ file_name: values.fileName });
    };

    return (
        <div>
            <div className='d-flex gap-2'>
                <span title={DIGITAL_LABOR_LAW_POSTER.VIEW} id='action-btn' onClick={viewHandler}>
                    <ViewIcon />
                </span>
                {hasPrivileges && (
                    <span
                        title={DIGITAL_LABOR_LAW_POSTER.DOWNLOAD}
                        id='action-btn'
                        onClick={downloadHandler}
                    >
                        <DownloadIcon />
                    </span>
                )}
                {hasPrivileges && (
                    <span
                        title={DIGITAL_LABOR_LAW_POSTER.EDIT}
                        id='action-btn'
                        onClick={editHandler}
                    >
                        <Edit />
                    </span>
                )}
                {hasPrivileges && (
                    <span
                        title={DIGITAL_LABOR_LAW_POSTER.DELETE}
                        id='action-btn'
                        onClick={() => setIsDeleteModalOpen(true)}
                    >
                        <Delete />
                    </span>
                )}
            </div>
            {isDeleteModalOpen && hasPrivileges && (
                <DeleteConfirmationModal
                    isDeleteModal={isDeleteModalOpen}
                    setIsDeleteModal={setIsDeleteModalOpen}
                    selectedItemId={id}
                    handleDeleteClick={deleteHandler}
                    deleteButtonName={file_name}
                    isLoading={deleteDLPMutation.isPending}
                />
            )}
            {showEditModal && hasPrivileges && (
                <EditFileNameModal
                    showEditModal={showEditModal}
                    setShowEditModal={setShowEditModal}
                    currentFileName={file_name}
                    onSubmit={editModalOnSubmit}
                    modalTitle='Rename Digital Labor Law Poster'
                    isLoading={editDLPMutation.isPending}
                />
            )}
        </div>
    );
};

export default DigitalLaborLawPosterActions;
